import { runInAction } from 'mobx';
import { Task } from 'redux-saga';
import { take, fork, cancelled } from 'redux-saga/effects';
import { Channel } from '../../../Shared/utils/user-action-channel';
import { callInAction, ActionHandlers, AsyncActionHandler, ActionConcurrencyInstruction, Action } from '../../../Shared/utils/saga-utils';
import {
	getVirtualTerminalDataService,
	VirtualTerminalActionRequest,
	VirtualTerminalActionResponse,
	VirtualTerminalApiConfigActions,
} from '../virtual-terminal-data-service';
import { VirtualTerminalApiConfig } from '../virtual-terminal-generated';
import { runSaga } from '../../utils/saga-utils';
import { reportVirtualTerminalError } from '../utils/virtual-terminal-error-utils';
import { isFunction } from '../../utils/is-function';

export interface IVirtualTerminalSagaContext {
	readonly userActionChannel: Channel<any>;
	readonly mainViewModel: any;
	readonly dataService: VirtualTerminalSagaDataService;
	executingBlockingAction?: boolean;
}

function getUserActionName<TActionType extends Action>(userAction: TActionType): string | undefined {
	return (userAction as any).constructor.name;
}

export function runVirtualTerminalSaga<TActionType extends Action>(
	sagaContext: IVirtualTerminalSagaContext,
	actionHandlers: ActionHandlers<IVirtualTerminalSagaContext, TActionType>,
	onSagaInit?: (context: IVirtualTerminalSagaContext) => void) {
	runSaga({}, virtualTerminalSaga, sagaContext, actionHandlers, onSagaInit);
}

function* virtualTerminalSaga<TActionType extends Action>(context: IVirtualTerminalSagaContext,
	actionHandlers: ActionHandlers<IVirtualTerminalSagaContext, TActionType>, onSagaInit?: (context: IVirtualTerminalSagaContext) => void): IterableIterator<any> {

	const { userActionChannel } = context;

	let currentAsyncHandler: AsyncActionHandler<IVirtualTerminalSagaContext> | undefined;
	let currentAsyncTask: Task | undefined;

	if (isFunction(onSagaInit)) {
		yield onSagaInit(context);
	}

	while (true) {
		const userAction: TActionType = yield take(userActionChannel);
		try {
			const incomingActionHandler = actionHandlers.get(userAction);
			console.log('userAction', getUserActionName(userAction));

			if (currentAsyncHandler && currentAsyncTask && currentAsyncTask.isRunning()) {
				const instruction = actionHandlers.getInstructionForIncomingAction(currentAsyncHandler, incomingActionHandler);

				switch (instruction) {
					case ActionConcurrencyInstruction.AllowIncoming:
						console.log('Allowing incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						break;
					case ActionConcurrencyInstruction.CancelRunning:
						console.log('Cancelling running action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						if (currentAsyncHandler.blocking) {
							context.executingBlockingAction = false;
						}
						currentAsyncTask.cancel();
						currentAsyncHandler = undefined;
						break;
					case ActionConcurrencyInstruction.BlockIncoming:
						console.log('Blocking incoming action', { running: currentAsyncHandler.actionType.name, incoming: incomingActionHandler.actionType.name });
						continue;
					default:
						throw new Error(`ActionConcurrencyInstruction[${instruction}] is not supported`);
				}
			}

			console.log('Starting action', { incoming: incomingActionHandler.actionType.name });
			if (incomingActionHandler.async) {
				currentAsyncHandler = incomingActionHandler;

				if (currentAsyncHandler.blocking) {
					context.executingBlockingAction = true;
				}

				currentAsyncTask = yield fork(function* () {
					try {
						yield callInAction(incomingActionHandler.handler, context, userAction);
					} catch (ex) {
						reportVirtualTerminalError(ex, {
							sagaRunningFork: true,
							sagaUserAction: getUserActionName(userAction),
						});
					} finally {
						if (!(yield cancelled())) {
							context.executingBlockingAction = false;
						}
					}
				});
			} else {
				runInAction(() => incomingActionHandler.handler(context, userAction));
			}
		} catch (ex) {
			reportVirtualTerminalError(ex, {
				sagaUserAction: getUserActionName(userAction),
			});
		}
	}
}

export type VirtualTerminalSagaDataService = {
	[x in keyof VirtualTerminalApiConfigActions]: (request: VirtualTerminalActionRequest<x>) => Iterator<VirtualTerminalActionResponse<x>>
};

export function sagaDataService(): VirtualTerminalSagaDataService {
	return (Object.keys(VirtualTerminalApiConfig.actions) as Array<keyof VirtualTerminalApiConfigActions>).reduce((acc: any, actionKey: keyof VirtualTerminalApiConfigActions) => {
		acc[actionKey] = function* (request: any) {
			const action: (...args: any[]) => PromiseLike<any> = getVirtualTerminalDataService()[actionKey];
			return yield action(request);
		};

		return acc;
	}, {});
}
